import type { IconName, Omittable } from "@salestrak/ui";

/**
 * Default width of the module rail column (icon + label tiles).
 *
 * The LABEL sets this floor, not the 36px chip: "Dashboards" is the widest
 * caption in `TILES` and measures 59px at the tile's 10px rung, so 64 is the
 * last width that still centers it without clipping — ~2.5px of air per side
 * (the same air "Workflows" at 51px had on the previous 56px rail). Below
 * this the one caption that cannot be shortened starts truncating. Captions
 * are what a rail costs; `MODULE_RAIL_WIDTH_COMPACT` is what it costs without
 * them.
 *
 * **A tile caption has a ~60px budget, and it should be the module's SHORT
 * name, not its full one.** The caption is otherwise an unbounded input
 * driving a fixed dimension — measured at this rung, Accounts (46), Contacts
 * (45), Reports (39), Calendar (44), Estimates (48) and Documents (56) fit,
 * while Integrations (59, tight) and Opportunities (68) do not. A module
 * whose name overflows gets a shorter caption here (Opportunities → "Deals",
 * Integrations → "Apps"); its full name lives on the module-menu pane title,
 * which is on screen whenever that module is active. "Dashboards" is the one
 * deliberate full-name exception, and it is what pins this width — do NOT
 * widen the rail past its caption, and do not let a caption truncate: a
 * clipped word is noise where a chosen short one is a label.
 */
export const MODULE_RAIL_WIDTH = 64;
/** Rail width in compact mode — tiles render icon-only, so only the 36px chip
 * and its 6px of air on each side remain. */
export const MODULE_RAIL_WIDTH_COMPACT = 48;

/**
 * Who a module is for. `member` marks a module that is a WORKSPACE-WIDE
 * surface — its roster, its map, its dashboards — and that a share-only
 * viewer (a role without public access: Guest, Limited Member, a custom role
 * flagged the same — `useViewerIsShareOnly`) never sees: no rail tile, no
 * More-popup row, no Customize row, no `G` chord, and the route itself
 * answers "You do not have access". A share-only viewer sees only what is
 * shared with them, and shared work reaches them through Home: a workflow
 * they hold a share on lists under Home's Workflows section, and its board
 * stays open to them even though the Workflows module does not. Unset means
 * every viewer.
 */
export type ModuleTier = "member";

export type RailTileData = Omittable<{
  id: string;
  icon: IconName;
  /** Filled counterpart shown only while this module owns the route. */
  selectedIcon: IconName;
  label: string;
  /** Workspace-relative route the tile navigates to (workspace prefix is
   * applied by `useSidebarNav`). */
  route: string;
  /** Static fallback badge. Live counts (e.g. Home's inbox count) are resolved
   * dynamically at render and take precedence — see `ModuleRail`. */
  badge?: string;
  /** False for modules without a module menu (the workspace layout resolves
   * their sidebar pane to null). Defaults to true. */
  hasSubmenu?: boolean;
  /**
   * Second key of this module's `G`-then-key chord, upper-cased. Letters are
   * unique across the whole registry, not just the tiles a viewer has shown:
   * the chord navigates from the registry, so a module hidden from the rail is
   * still reachable, and a duplicate would make one of them unreachable.
   */
  hotkey?: string;
  /** See `ModuleTier`. */
  tier?: ModuleTier;
}>;

// Static tile registry — the source of truth for each tile's presentation
// and routing, in default order. The user's `railTiles` preference reorders
// and hides these (resolved in `useNavPreferences`); the rail renders the
// resolved list from `ModuleRailContext`, never this array directly.
export const TILES: RailTileData[] = [
  {
    id: "home",
    icon: "home",
    selectedIcon: "home-filled",
    label: "Home",
    hotkey: "H",
    route: "/",
  },
  {
    id: "map",
    icon: "map-pin",
    selectedIcon: "map-pin-filled",
    label: "Map",
    hotkey: "M",
    route: "/map",
    tier: "member",
  },
  {
    id: "workflows",
    icon: "workflow",
    selectedIcon: "workflow-filled",
    label: "Workflows",
    hotkey: "W",
    route: "/workflows",
    tier: "member",
  },
  {
    id: "teams",
    icon: "users",
    selectedIcon: "users-filled",
    label: "Teams",
    hotkey: "T",
    route: "/teams",
    tier: "member",
  },
  {
    id: "dashboards",
    icon: "bar-chart",
    selectedIcon: "bar-chart-filled",
    // The one full-name caption over the short-name guidance: no short word
    // says "dashboards", so this label is what sets MODULE_RAIL_WIDTH (see
    // its doc comment before adding anything wider).
    label: "Dashboards",
    hotkey: "D",
    route: "/dashboards",
    tier: "member",
  },
];

/** The chord's leading key. `G` for "go to", the verb the tooltips use. */
export const MODULE_HOTKEY_LEAD = "G";

/**
 * A screen inside a module that earns a `G` chord of its own. Not a rail
 * tile — no chip, no caption, no customize row — just a place people go often
 * enough that "G H, then click the row" is a press too many. Letters share
 * ONE namespace with `RailTileData.hotkey`: a screen's letter is taken from
 * the same alphabet the tiles draw from, and a duplicate would shadow one of
 * them (asserted by the module-hotkeys test).
 */
export type ScreenHotkeyData = {
  id: string;
  icon: IconName;
  label: string;
  /** Second key of the chord, upper-cased. */
  hotkey: string;
  /** Workspace-relative route, like a tile's. */
  route: string;
};

/**
 * Screens a module menu lists that have no rail tile of their own, as the
 * document title names them. The menu rows phrase the same screens as
 * directories ("All Members"); the tab title carries the screen's own name.
 * `route` is the workspace-relative path.
 */
export const MODULE_SCREENS: readonly { route: string; label: string }[] = [
  { route: "/my-work", label: "My Work" },
  { route: "/members", label: "Members" },
  { route: "/org-chart", label: "Org Chart" },
];

export const SCREEN_HOTKEYS: ScreenHotkeyData[] = [
  { id: "inbox", icon: "inbox", label: "Inbox", hotkey: "I", route: "/inbox" },
  // Settings belongs to no module (`tileIdForRouteSegment` returns null for
  // it), so a chord is its only keyboard route in. `/settings` redirects to
  // Preferences — the same landing the account menu's Settings item uses.
  {
    id: "settings",
    icon: "settings",
    label: "Settings",
    hotkey: "S",
    route: "/settings",
  },
];

/**
 * Whether a module is one this viewer may reach at all. The ONE reading of
 * `tier` — the rail context filters its tile list through it, the hotkey hook
 * and the shortcuts reference filter the chord registry through it, and the
 * route guards (`RequireMemberModule`) decide through the same share-only
 * reading, so a share-only viewer cannot find a member module by any door.
 */
export function tileVisibleToViewer(
  tile: { tier?: ModuleTier | undefined },
  viewerIsShareOnly: boolean,
): boolean {
  return !(viewerIsShareOnly && tile.tier === "member");
}

/**
 * The chord destinations a given viewer can actually use — `GO_TO_DESTINATIONS`
 * minus the member modules for a share-only viewer. The shortcuts reference
 * renders this so it never advertises a letter the hook ignores for the
 * viewer reading it.
 */
export function goToDestinationsForViewer(
  viewerIsShareOnly: boolean,
): readonly { label: string; hotkey: string }[] {
  return [
    ...TILES.flatMap((tile) =>
      tile.hotkey && tileVisibleToViewer(tile, viewerIsShareOnly)
        ? [{ label: tile.label, hotkey: tile.hotkey }]
        : [],
    ),
    ...SCREEN_HOTKEYS.map((screen) => ({
      label: screen.label,
      hotkey: screen.hotkey,
    })),
  ];
}

/**
 * Every `G`-chord destination as the label/letter pair: module tiles in rail
 * order, then screens. The letter-uniqueness check reads this full set; the
 * shortcuts reference reads `goToDestinationsForViewer`, which drops the
 * member modules for a share-only viewer so it cannot list a letter the hook
 * ignores.
 */
export const GO_TO_DESTINATIONS: readonly { label: string; hotkey: string }[] =
  goToDestinationsForViewer(false);

/** Workspace state a tile's entry route depends on, beyond the registry. */
export type ModuleRouteContext = {
  /** The workflow the Workflows tile deep-links to (`useWorkflowsLanding`:
   * the last one opened, else the first listed); undefined falls back to
   * the roster page. */
  workflowId: string | undefined;
};

/**
 * Resolve a module's preferred entry route from current workspace state.
 *
 * Home targets My Work, the module's first screen, rather than the workspace
 * root: the root is a pure redirect to `preferences.defaultRoute` — a LAUNCH
 * preference that may name another module entirely (the Map) — so a Home tile
 * pointed at it is a proxy for whatever that preference says, and with a
 * non-Home default the Home module is unreachable from its own tile.
 */
export function resolveModuleRoute(
  tile: RailTileData,
  context: ModuleRouteContext,
): string {
  // The workspace root is a pure redirect to `preferences.defaultRoute`, a
  // LAUNCH preference that may name another module entirely (the Map). A Home
  // tile pointed at the root is a proxy for whatever that preference says, so
  // a non-Home default leaves the Home module unreachable from its own tile.
  // The tile targets My Work, the module's first screen, directly.
  if (tile.id === "home") return "/my-work";
  if (tile.id === "workflows" && context.workflowId) {
    return `/workflow/${context.workflowId}`;
  }
  return tile.route;
}

/**
 * Where `G` then `key` lands, or null when the letter belongs to nothing.
 * Tiles win over screens and resolve through `resolveModuleRoute`, so `G W`
 * opens the first workflow's board exactly as pressing the tile does. For a
 * share-only viewer (`viewerIsShareOnly`, the `useViewerIsShareOnly`
 * reading) a member module's letter belongs to nothing, exactly as its tile
 * is not on their rail.
 */
export function resolveHotkeyRoute(
  key: string,
  context: ModuleRouteContext,
  viewerIsShareOnly: boolean,
): string | null {
  const tile = TILES.find((t) => t.hotkey === key);
  if (tile) {
    return tileVisibleToViewer(tile, viewerIsShareOnly)
      ? resolveModuleRoute(tile, context)
      : null;
  }
  const screen = SCREEN_HOTKEYS.find((s) => s.hotkey === key);
  return screen ? screen.route : null;
}

/**
 * Resolve which tile owns a route, given the route segment that follows
 * `[workspace]` (from `useSegments`). The active tile is DERIVED from the
 * route — it survives reloads and back/forward navigation for free.
 *
 * Module ownership is broader than the tiles' entry `route`s: a module owns
 * every screen reachable from its module menu (e.g. Home owns `/inbox`, Teams
 * owns `/members`). Settings routes belong to no module (they have their own
 * dedicated sidebar) — null clears the tile highlight.
 */
export function tileIdForRouteSegment(
  segment: string | undefined,
): string | null {
  switch (segment) {
    case undefined: // workspace index — Home's "My Work"
    case "index":
    case "inbox":
    case "my-work":
    case "shared": // Home's "Shared with me" list
      return "home";
    case "map":
      return "map";
    case "workflows": // the workflows roster (/workflows)
    case "workflow": // a single workflow's views (/workflow/[id])
      return "workflows";
    case "dashboards":
      return "dashboards";
    case "teams":
    case "team":
    case "members":
    // All-Members route segment is the singular `member`; member views share the
    // Teams sidebar (decision #5).
    case "member":
    // The workspace org chart is a Teams-module destination (its nav row
    // lives in TeamsNavSection).
    case "org-chart":
      return "teams";
    default:
      // settings and anything unrecognized: no module owns it.
      return null;
  }
}
